/**
 * The zoom ladder — PV-3.
 *
 * Fixed steps rather than free scaling, for two reasons that both show up on
 * screen: a percentage the label can print exactly, and a scale that lands on
 * a whole number of device pixels often enough that hairlines in a tab stay
 * hairlines.
 *
 * Pure, and separate from the viewer, because clamping is the part that has
 * consequences and the part a component test would exercise only by accident.
 * Below 50% a page of notation is unreadable; above 300% a 660px page becomes
 * a canvas big enough to make a phone stutter, and the browser is the thing
 * that reports that, several seconds later, as a hang.
 */

/** Whole percentages only — the chrome label has no room for a decimal. */
export const ZOOM_STEPS = [50, 75, 100, 125, 150, 200, 250, 300] as const;

export const DEFAULT_ZOOM = 100;

export const MIN_ZOOM = ZOOM_STEPS[0];
export const MAX_ZOOM = ZOOM_STEPS[ZOOM_STEPS.length - 1];

/**
 * The next step up or down, clamped at both ends.
 *
 * Clamping rather than wrapping: a reader pressing `+` past the top expects
 * nothing to happen, and a jump back to 50% reads as a bug in a control they
 * were using deliberately.
 */
export function stepZoom(current: number, direction: 1 | -1): number {
  const index = ZOOM_STEPS.indexOf(current as (typeof ZOOM_STEPS)[number]);

  /*
   * An unknown value lands on the nearest step rather than throwing or
   * defaulting to 100%. It should not happen - the ladder is the only source
   * of these numbers - but "should not happen" state that resets the reader's
   * zoom to the middle is worse than one that carries on from where they are.
   */
  if (index === -1) return nearestStep(current);

  const next = index + direction;
  if (next < 0 || next >= ZOOM_STEPS.length) return current;
  return ZOOM_STEPS[next];
}

export function canZoom(current: number, direction: 1 | -1): boolean {
  return stepZoom(current, direction) !== current;
}

function nearestStep(value: number): number {
  return ZOOM_STEPS.reduce((best, step) =>
    Math.abs(step - value) < Math.abs(best - value) ? step : best
  );
}

/** The scale pdf.js is given. The ladder is in percent; viewports are not. */
export const scaleFor = (zoom: number): number => zoom / 100;

/**
 * The largest step whose rendered page fits the height available — PV-4.
 *
 * Expanding the viewer without changing the zoom gives a bigger desk and the
 * same small page, which is not what anyone means by fullscreen. This picks the
 * step that actually uses the room.
 *
 * Pure, and given numbers rather than elements, because jsdom reports every
 * measurement as 0 — so the arithmetic is testable here and the measuring is
 * the component's problem.
 *
 * @param pageHeightAtScale1 the page's height in CSS pixels at 100%
 * @param available the height the page has to fit into
 */
export function fitZoom(pageHeightAtScale1: number, available: number): number {
  // jsdom, a collapsed container, or a document that has not opened yet. Any
  // of them would otherwise pick the floor and shrink the page on expand.
  if (!(pageHeightAtScale1 > 0) || !(available > 0)) return DEFAULT_ZOOM;

  const ideal = (available / pageHeightAtScale1) * 100;

  let best: number = ZOOM_STEPS[0];
  for (const step of ZOOM_STEPS) {
    if (step <= ideal) best = step;
  }
  return best;
}
